import { writeWorkbook, downloadBlob, safeSheetName } from './xlsx';

// THE ACCOUNTING MAP AS A SPREADSHEET — written here, read back here, checked here.
//
// The map is prepared in Excel, not on the screen: a property's finance team fills in accounting codes, GL
// accounts and categories against the transaction codes their PMS returns, and hands back one file. This
// module is the whole of that conversation — the workbook that goes out, and the check the file has to
// pass before it is allowed to replace what is stored.
//
// THE COLUMNS ARE NOT DECLARED HERE. They come from the server with the rows (GET …/conversion-codes →
// `columns`, `contextColumns`), so the file, the screen and the store are built from ONE list. A second
// copy of the column names in the portal is exactly how a required column quietly stops being required.
//
// REQUIRED MEANS REQUIRED. A row missing any required value makes the whole file invalid: the upload is a
// full replacement, so a file that is half right would replace a good map with a worse one and nothing
// afterwards would say which rows came from where. The server checks it again for the same reason.

/** Headers are matched loosely — case, spaces and underscores vary between saves and hands. */
const norm = (h) => String(h || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const blank = (v) => v === undefined || v === null || String(v).trim() === '';

/** The sheet a code map is written to and read from. */
/**
 * Is this column required FOR THIS ROW?
 *
 * `required` alone cannot answer it. A column may be required in general and legitimately blank when
 * another column says so — CATEGORY_ID is required on a revenue code and empty on a payment, because a
 * payment has no revenue category to belong to and OPERA exports it blank.
 *
 * THE RULE IS NOT WRITTEN HERE. It arrives on the column itself as
 * `optionalWhen: { field, equalsAny: [...] }`, declared once in the util layer beside the column list
 * and served with it (GET …/conversion-codes). This is only the reading of it — so a new exemption is a
 * change to the contract, not a change to four validators.
 *
 * Compared case- and space-insensitively: the value comes out of somebody's spreadsheet, where
 * 'Payments', 'PAYMENTS' and 'payments ' are the same answer.
 */
export const isColumnRequired = (col, row) => {
    if (!col || !col.required) return false;
    const rule = col.optionalWhen;
    if (!rule || !rule.field || !Array.isArray(rule.equalsAny)) return true;
    const actual = String((row && row[rule.field]) ?? '').trim().toUpperCase();
    return !rule.equalsAny.some(v => String(v).trim().toUpperCase() === actual);
};

export const SHEET_NAME = safeSheetName('Accounting Codes');

/**
 * The workbook for a set of codes: every required column, then every context column, in the order the
 * server declares them. Context values the user gave us last time come back exactly as they were —
 * downloading and re-uploading unchanged must be a no-op, or the file stops being a safe way to work.
 */
export const downloadCodeWorkbook = async ({ columns, contextColumns, codes, filename }) => {
    const headers = [
        ...columns.map(c => c.header),
        ...contextColumns,
    ];
    const rows = (codes || []).map(code => ([
        ...columns.map(c => (code[c.field] == null ? '' : String(code[c.field]))),
        ...contextColumns.map(h => {
            const v = (code.context || {})[h];
            return v == null ? '' : String(v);
        }),
    ]));
    const blob = await writeWorkbook([{ name: SHEET_NAME, columns: headers, rows }]);
    downloadBlob(blob, filename);
};

/**
 * Read a workbook back into rows the API will accept.
 *
 * @returns {{ rows: Array, errors: string[], missingColumns: string[], sheet: string, ignored: string[] }}
 *          `rows` is only meaningful when `errors` is empty.
 */
export const parseCodeWorkbook = (book, { columns, contextColumns }) => {
    const errors = [];
    // The first sheet with anything on it. Naming the sheet would be one more thing to get wrong in a file
    // somebody re-saved from Numbers.
    const sheetName = Object.keys(book).find(n => (book[n] || []).length > 1) || Object.keys(book)[0];
    const grid = (book[sheetName] || []).filter(r => r && r.some(v => !blank(v)));
    if (grid.length < 2) {
        return { rows: [], errors: ['The sheet has no rows under its header.'], missingColumns: [], sheet: sheetName, ignored: [] };
    }

    const header = grid[0].map(norm);
    const indexOf = (h) => header.indexOf(norm(h));

    const missingColumns = columns.filter(c => indexOf(c.header) === -1).map(c => c.header);
    if (missingColumns.length) {
        return { rows: [], errors: [], missingColumns, sheet: sheetName, ignored: [] };
    }

    // Columns in the file that are neither required nor a context column we know. Reported, not refused —
    // a working sheet often carries a scratch column, and rejecting the file over one would be tyranny.
    const known = new Set([...columns.map(c => norm(c.header)), ...contextColumns.map(norm)]);
    const ignored = grid[0]
        .filter(h => !blank(h) && !known.has(norm(h)))
        .map(h => String(h).trim());

    const rows = [];
    const seen = new Map();
    grid.slice(1).forEach((cells, i) => {
        const lineNo = i + 2; // row 1 is the header, in the file the person is looking at
        const row = {};
        for (const col of columns) row[col.field] = (cells[indexOf(col.header)] || '').trim();

        // Per row, not per file: which columns are required depends on what this row says (a payment
        // has no CATEGORY_ID).
        const missing = columns.filter(c => isColumnRequired(c, row) && blank(row[c.field])).map(c => c.header);
        if (missing.length) {
            errors.push(`Row ${lineNo}: ${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} empty`);
            return;
        }

        // ONE ROW PER CODE. Two rows for the same TRX_CODE means one of them silently wins on upload —
        // which is discovered months later as a code mapped to the wrong account.
        const key = row[columns[0].field].toUpperCase();
        if (seen.has(key)) {
            errors.push(`Row ${lineNo}: ${columns[0].header} ${row[columns[0].field]} is already on row ${seen.get(key)}`);
            return;
        }
        seen.set(key, lineNo);

        const context = {};
        for (const h of contextColumns) {
            const at = indexOf(h);
            if (at === -1) continue;
            const v = (cells[at] || '').trim();
            if (v) context[h] = v;
        }
        row.context = context;
        rows.push(row);
    });

    return { rows, errors, missingColumns: [], sheet: sheetName, ignored };
};
